import { onAuthStateChanged, signOut } from 'firebase/auth';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { auth } from './firebase/client';
import { LoginPage } from './pages/LoginPage';
import { WorkspaceSetupPage } from './pages/WorkspaceSetupPage';
import { AppDialogHost } from './components/AppDialogHost';
import { ToastHost } from './components/ToastHost';
import { WebLogoSplash } from './components/WebLogoSplash';
import { useWebSplash } from './context/WebSplashContext';
import { isWebSplashEnabled } from './lib/webSplash';
import { getCompaniesForUser } from './services/companyService';
import { MembershipAccessGate } from './components/MembershipAccessGate';

const MainShell = lazy(() =>
  import('./pages/MainShell').then((m) => ({ default: m.MainShell })),
);

export default function App() {
  const splashEnabled = isWebSplashEnabled();
  const { active: splashTimerActive } = useWebSplash();
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(() => auth().currentUser);
  const [hasWorkspaces, setHasWorkspaces] = useState<boolean | null>(null);
  const [shellReady, setShellReady] = useState(!splashEnabled);

  useEffect(() => {
    if (!splashEnabled) return;
    void import('./pages/MainShell').then(() => setShellReady(true));
  }, [splashEnabled]);

  useEffect(() => {
    return onAuthStateChanged(auth(), (u) => {
      setUser(u);
      if (!u?.uid) {
        setHasWorkspaces(null);
        setReady(true);
        return;
      }
      const phone = u.phoneNumber?.trim();
      if (!phone) {
        void signOut(auth());
        setHasWorkspaces(null);
        setReady(true);
        return;
      }
      setReady(false);
      getCompaniesForUser(u.uid)
        .then((list) => {
          setHasWorkspaces(list.length > 0);
          setReady(true);
        })
        .catch(() => {
          setHasWorkspaces(false);
          setReady(true);
        });
    });
  }, []);

  const needsMainShell = Boolean(user && hasWorkspaces);
  const showSplash =
    splashEnabled &&
    (splashTimerActive || !ready || (needsMainShell && !shellReady));

  if (showSplash) {
    return (
      <div className="app-routes-fill">
        <WebLogoSplash />
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="app-loading-screen">
        <span style={{ color: 'var(--hk-muted)' }}>Loading…</span>
      </div>
    );
  }

  return (
    <div className="app-routes-fill">
      <AppDialogHost />
      <ToastHost />
      <Routes>
      <Route
        path="/login"
        element={
          user ? (
            <Navigate
              to={hasWorkspaces ? '/' : '/workspace-setup'}
              replace
            />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route
        path="/workspace-setup"
        element={
          !user ? (
            <Navigate to="/login" replace />
          ) : hasWorkspaces ? (
            <Navigate to="/" replace />
          ) : (
            <WorkspaceSetupPage />
          )
        }
      />
      <Route
        path="/*"
        element={
          !user ? (
            <Navigate to="/login" replace />
          ) : !hasWorkspaces ? (
            <Navigate to="/workspace-setup" replace />
          ) : (
            <Suspense fallback={splashEnabled ? <WebLogoSplash /> : (
                <div className="app-loading-screen">
                  <span style={{ color: 'var(--hk-muted)' }}>Loading…</span>
                </div>
              )}>
              <MembershipAccessGate uid={user.uid}>
                <MainShell />
              </MembershipAccessGate>
            </Suspense>
          )
        }
      />
    </Routes>
    </div>
  );
}
